// pages/Connector/OneDrive/OneDriveConnector.js
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import { Box, Typography, Alert } from '@mui/material';
import ListOneDriveFiles from "./ListOneDriveFiles";

export default function OneDriveConnector() {
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState('');
    const [accessToken, setAccessToken] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [isPickerLoading, setIsPickerLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const { chatbotId : paramsChatbotId } = useParams();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('connectors', 'view');
                const modifyPerm = await hasPermission('connectors', 'add|edit');
                const deletePerm = await hasPermission('connectors', 'delete');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
                setCanDelete(deletePerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
                setCanDelete(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);
    
    const checkStatus = useCallback(async (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        setError('');
        
        
        try {

            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

            const response = await axiosInstance.get(`/api/onedrive/status`, {
                timeout: 30000,
                params
            });
            
            setIsConnected(response.data.connected);
            setAccessToken(response.data.accessToken || null);
            
            if (!response.data.connected) {
                const params = new URLSearchParams(location.search);
                if (params.has('error') || params.has('connected')) {
                    navigate(location.pathname, { replace: true });
                }
            }
        } catch (err) {
            console.error("Error checking OneDrive status:", err);
            setError("Failed to check connection status. Please try again.");
            setIsConnected(false);
            setAccessToken(null);
        } finally {
            setIsLoading(false);
        }
    }, [location.pathname, location.search, navigate]);

    useEffect(() => {
        document.title = 'OneDrive Connector | KlonIT';
        const params = new URLSearchParams(location.search);
        
        if (params.has('connected') && params.get('connected') === 'true') {
            console.log("Successfully connected to OneDrive via redirect.");
            navigate(location.pathname, { replace: true });
            checkStatus(false);
            setRefreshTrigger(prev => prev + 1);
        } else if (params.has('error')) {
            setError(`Authentication failed after redirect: ${params.get('error')}`);
            navigate(location.pathname, { replace: true });
        } else {
            checkStatus();
        }
    }, [checkStatus, location.pathname, location.search, navigate]);

    const handleConnect = async () => {
        setIsConnecting(true);
        setError('');
        

        try {

            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/api/onedrive/auth/start`, { params } );
            if (response.data && response.data.success && response.data.authorization_url) {
                window.location.href = response.data.authorization_url;
            } else {
                console.error("Backend returned error for auth/start:", response.data);
                setError(response.data.message || "Failed to initiate OneDrive connection. Please try again.");
            }
        } catch (err) {
            console.error("Error calling /auth/start:", err);
            if (err.response && err.response.data && err.response.data.message) {
                setError(err.response.data.message);
            } else {
                setError("Could not connect to OneDrive. Please check your network and try again.");
            }
        } finally {
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setIsConnecting(true);
        setError('');
        
        
        try {

            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            await axiosInstance.post(`/api/onedrive/disconnect`, {}, { params });
            
            setIsConnected(false);
            setAccessToken(null);
            setRefreshTrigger(prev => prev + 1);
            setError('Successfully disconnected from OneDrive.');
        } catch (err) {
            console.error("Error disconnecting OneDrive:", err);
            setError(err.response?.data?.message || "Failed to disconnect. Please try again.");
        } finally {
            setIsConnecting(false);
        }
    };

    const handleSync = async (fileIds) => {
        if (!fileIds || fileIds.length === 0) {
            setError("No files selected to sync.");
            return;
        }

        setIsLoading(true);
        setError('');
        const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
        try {
            const response = await axiosInstance.post(`/api/onedrive/sync`, { fileIds }, {
                params
            });
            console.log("Sync response:", response.data);
            setError(response.data.message || 'Files synced successfully.');
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            console.error("Error syncing OneDrive files:", err);
            setError(err.response?.data?.message || "Failed to sync files. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Enhanced OneDrive File Picker with modern UI
    const openOneDriveFilePicker = useCallback(() => {
        if (!accessToken) {
            setError("OneDrive not connected. Please connect first.");
            return;
        }

        setIsPickerLoading(true);
        
        // Create custom file browser popup
        const popupFeatures = [
            'width=1200',
            'height=800',
            'scrollbars=yes',
            'resizable=yes',
            'toolbar=no',
            'menubar=no',
            'location=no',
            'status=no'
        ].join(',');

        const pickerWindow = window.open('about:blank', 'OneDrivePicker', popupFeatures);
        
        if (!pickerWindow) {
            setError('Popup was blocked. Please allow popups for this site and try again.');
            setIsPickerLoading(false);
            return;
        }

        // Create OneDrive-style UI
        pickerWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>OneDrive</title>
                <meta charset="utf-8">
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    
                    body { 
                        font-family: "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, sans-serif;
                        background: #faf9f8; 
                        color: #323130; 
                        height: 100vh; 
                        display: flex; 
                        flex-direction: column;
                    }

                    .header {
                        background: #ffffff; 
                        border-bottom: 1px solid #edebe9; 
                        padding: 12px 24px;
                        display: flex; 
                        align-items: center; 
                        justify-content: space-between; 
                        min-height: 48px;
                    }

                    .onedrive-logo { 
                        display: flex; 
                        align-items: center; 
                        gap: 8px; 
                        font-weight: 600; 
                        color: #0078d4; 
                    }
                    
                    .logo-icon { 
                        width: 20px; 
                        height: 20px; 
                        background: linear-gradient(135deg, #0078d4, #1ba1e2); 
                        border-radius: 3px; 
                    }

                    .close-btn { 
                        background: none; 
                        border: none; 
                        font-size: 16px; 
                        padding: 8px; 
                        border-radius: 4px; 
                        cursor: pointer; 
                    }
                    
                    .close-btn:hover { 
                        background: #f3f2f1; 
                    }

                    .nav-section { 
                        background: white; 
                        border-bottom: 1px solid #edebe9; 
                        padding: 12px 24px; 
                    }
                    
                    .breadcrumb { 
                        display: flex; 
                        align-items: center; 
                        gap: 4px; 
                        font-size: 14px; 
                        color: #605e5c; 
                    }
                    
                    .breadcrumb-item { 
                        padding: 4px 8px; 
                        border-radius: 4px; 
                        cursor: pointer; 
                    }
                    
                    .breadcrumb-item:hover { 
                        background: #f3f2f1; 
                    }
                    
                    .breadcrumb-separator { 
                        color: #a19f9d; 
                        margin: 0 4px; 
                    }

                    .main-content { 
                        flex: 1; 
                        display: flex; 
                        flex-direction: column; 
                        overflow: hidden; 
                    }
                    
                    .file-area { 
                        flex: 1; 
                        overflow-y: auto; 
                        background: white; 
                        margin: 24px; 
                        border-radius: 8px; 
                        border: 1px solid #edebe9; 
                    }

                    .file-header {
                        display: grid; 
                        grid-template-columns: 1fr 120px 100px 140px 40px; 
                        gap: 16px; 
                        padding: 12px 24px;
                        background: #faf9f8; 
                        border-bottom: 1px solid #edebe9; 
                        font-size: 12px; 
                        font-weight: 600;
                        color: #605e5c; 
                        text-transform: uppercase; 
                        letter-spacing: 0.5px;
                    }

                    .file-list { 
                        min-height: 200px; 
                    }
                    
                    .file-item {
                        display: grid; 
                        grid-template-columns: 1fr 120px 100px 140px 40px; 
                        gap: 16px; 
                        padding: 12px 24px;
                        border-bottom: 1px solid #f3f2f1; 
                        cursor: pointer; 
                        transition: all 0.1s ease; 
                        align-items: center;
                    }
                    
                    .file-item:hover { 
                        background: #f8f7f6; 
                    }
                    
                    .file-item.selected { 
                        background: #eff6fc; 
                        border-left: 3px solid #0078d4; 
                        padding-left: 21px; 
                    }

                    .file-main { 
                        display: flex; 
                        align-items: center; 
                        gap: 12px; 
                        min-width: 0; 
                    }
                    
                    .file-icon { 
                        width: 20px; 
                        height: 20px; 
                        flex-shrink: 0; 
                        font-size: 16px; 
                    }
                    
                    .file-name { 
                        font-weight: 400; 
                        color: #323130; 
                        overflow: hidden; 
                        text-overflow: ellipsis; 
                        white-space: nowrap; 
                    }
                    
                    .file-type, .file-size, .file-date { 
                        font-size: 12px; 
                        color: #605e5c; 
                    }
                    
                    .file-checkbox { 
                        width: 16px; 
                        height: 16px; 
                        margin: 0; 
                        cursor: pointer; 
                    }

                    .loading { 
                        display: flex; 
                        flex-direction: column; 
                        align-items: center; 
                        padding: 60px 20px; 
                        color: #605e5c; 
                    }
                    
                    .loading-spinner {
                        width: 24px; 
                        height: 24px; 
                        border: 2px solid #edebe9; 
                        border-top: 2px solid #0078d4;
                        border-radius: 50%; 
                        animation: spin 1s linear infinite; 
                        margin-bottom: 16px;
                    }
                    
                    @keyframes spin { 
                        0% { transform: rotate(0deg); } 
                        100% { transform: rotate(360deg); } 
                    }

                    .footer {
                        background: white; 
                        border-top: 1px solid #edebe9; 
                        padding: 16px 24px;
                        display: flex; 
                        align-items: center; 
                        justify-content: space-between;
                    }

                    .btn {
                        padding: 8px 20px; 
                        border-radius: 4px; 
                        font-size: 14px; 
                        font-weight: 600; 
                        cursor: pointer;
                        transition: all 0.1s ease; 
                        border: 1px solid transparent;
                    }
                    
                    .btn-primary { 
                        background: #0078d4; 
                        color: white; 
                        border-color: #0078d4; 
                    }
                    
                    .btn-primary:hover:not(:disabled) { 
                        background: #106ebe; 
                    }
                    
                    .btn-primary:disabled { 
                        background: #f3f2f1; 
                        color: #a19f9d; 
                        cursor: not-allowed; 
                    }
                    
                    .btn-secondary { 
                        background: white; 
                        color: #323130; 
                        border-color: #edebe9; 
                    }
                    
                    .btn-secondary:hover { 
                        background: #f3f2f1; 
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="onedrive-logo">
                        <div class="logo-icon"></div>
                        OneDrive
                    </div>
                    <button class="close-btn" onclick="window.close()">&times;</button>
                </div>

                <div class="nav-section">
                    <div class="breadcrumb" id="breadcrumb">
                        <span class="breadcrumb-item">My files</span>
                    </div>
                </div>

                <div class="main-content">
                    <div class="file-area">
                        <div class="file-header">
                            <div>Name</div>
                            <div>Type</div>
                            <div>Size</div>
                            <div>Modified</div>
                            <div></div>
                        </div>
                        <div class="file-list" id="fileList">
                            <div class="loading">
                                <div class="loading-spinner"></div>
                                <div>Loading your OneDrive files...</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="footer">
                    <div id="selectedCount">0 files selected</div>
                    <div>
                        <button class="btn btn-secondary" onclick="window.close()" style="margin-right: 8px;">Cancel</button>
                        <button class="btn btn-primary" id="selectBtn" disabled onclick="selectFiles()">Add files</button>
                    </div>
                </div>

                <script>
                    let selectedFiles = new Set();
                    let currentFiles = [];
                    let pathStack = [{id: 'root', name: 'My files'}];
                    
                    function getFileIcon(file) {
                        if (file.folder) return '📁';
                        const ext = file.name.split('.').pop()?.toLowerCase();
                        const icons = {
                            pdf: '📄', doc: '📄', docx: '📄', txt: '📄',
                            xls: '📊', xlsx: '📊', csv: '📊',
                            ppt: '📊', pptx: '📊',
                            jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
                            mp4: '🎥', avi: '🎥', mov: '🎥',
                            mp3: '🎵', wav: '🎵', flac: '🎵',
                            zip: '📦', rar: '📦', '7z': '📦',
                            js: '💻', html: '💻', css: '💻', py: '💻',
                        };
                        return icons[ext] || '📄';
                    }
                    
                    function getFileType(file) {
                        if (file.folder) return 'Folder';
                        const ext = file.name.split('.').pop()?.toLowerCase();
                        const types = {
                            pdf: 'PDF', doc: 'Word', docx: 'Word', txt: 'Text',
                            xls: 'Excel', xlsx: 'Excel', csv: 'CSV',
                            ppt: 'PowerPoint', pptx: 'PowerPoint',
                            jpg: 'JPEG', jpeg: 'JPEG', png: 'PNG', gif: 'GIF',
                            mp4: 'MP4', avi: 'AVI', mov: 'MOV',
                            mp3: 'MP3', wav: 'WAV',
                            zip: 'ZIP', rar: 'RAR',
                            js: 'JavaScript', html: 'HTML', css: 'CSS',
                        };
                        return types[ext] || (ext ? ext.toUpperCase() : 'File');
                    }

                    async function fetchFiles(folderId = 'root') {
                        try {
                            const endpoint = folderId === 'root' 
                                ? 'https://graph.microsoft.com/v1.0/me/drive/root/children'
                                : \`https://graph.microsoft.com/v1.0/me/drive/items/\${folderId}/children\`;
                                
                            const response = await fetch(endpoint + '?$top=100&$select=id,name,size,file,folder,webUrl,lastModifiedDateTime', {
                                headers: { 
                                    'Authorization': 'Bearer ${accessToken}', 
                                    'Content-Type': 'application/json' 
                                }
                            });
                            
                            if (!response.ok) {
                                throw new Error('Failed to fetch files');
                            }
                            
                            const data = await response.json();
                            return data.value || [];
                        } catch (error) {
                            console.error('Error fetching files:', error);
                            throw error;
                        }
                    }
                    
                    function formatFileSize(bytes) {
                        if (!bytes) return '—';
                        const sizes = ['B', 'KB', 'MB', 'GB'];
                        const i = Math.floor(Math.log(bytes) / Math.log(1024));
                        return Math.round(bytes / Math.pow(1024, i) * 10) / 10 + ' ' + sizes[i];
                    }
                    
                    function formatDate(dateString) {
                        if (!dateString) return '—';
                        const date = new Date(dateString);
                        const now = new Date();
                        const diffDays = Math.ceil(Math.abs(now - date) / (1000 * 60 * 60 * 24));
                        if (diffDays === 1) return 'Yesterday';
                        if (diffDays < 7) return \`\${diffDays} days ago\`;
                        return date.toLocaleDateString();
                    }
                    
                    function renderFiles(files) {
                        currentFiles = files;
                        const fileList = document.getElementById('fileList');
                        
                        if (files.length === 0) {
                            fileList.innerHTML = \`
                                <div class="loading">
                                    <div style="font-size: 48px; margin-bottom: 16px;">📂</div>
                                    <div>This folder is empty</div>
                                </div>
                            \`;
                            return;
                        }
                        
                        const sortedFiles = [...files].sort((a, b) => {
                            if (a.folder && !b.folder) return -1;
                            if (!a.folder && b.folder) return 1;
                            return a.name.localeCompare(b.name);
                        });
                        
                        fileList.innerHTML = sortedFiles.map(file => \`
                            <div class="file-item" data-file-id="\${file.id}" onclick="handleFileClick('\${file.id}')">
                                <div class="file-main">
                                    <div class="file-icon">\${getFileIcon(file)}</div>
                                    <div class="file-name" title="\${file.name}">\${file.name}</div>
                                </div>
                                <div class="file-type">\${getFileType(file)}</div>
                                <div class="file-size">\${formatFileSize(file.size)}</div>
                                <div class="file-date">\${formatDate(file.lastModifiedDateTime)}</div>
                                <div>
                                    \${!file.folder ? \`<input type="checkbox" class="file-checkbox" onclick="event.stopPropagation(); toggleSelection('\${file.id}')">\` : ''}
                                </div>
                            </div>
                        \`).join('');
                        
                        // Restore checkbox states
                        selectedFiles.forEach(fileId => {
                            const item = document.querySelector(\`[data-file-id="\${fileId}"]\`);
                            const checkbox = item?.querySelector('.file-checkbox');
                            if (item) item.classList.add('selected');
                            if (checkbox) checkbox.checked = true;
                        });
                    }
                    
                    function handleFileClick(fileId) {
                        const file = currentFiles.find(f => f.id === fileId);
                        if (file.folder) {
                            pathStack.push({id: fileId, name: file.name});
                            updateBreadcrumb();
                            loadFiles(fileId);
                        } else {
                            toggleSelection(fileId);
                        }
                    }
                    
                    function toggleSelection(fileId) {
                        const checkbox = document.querySelector(\`[data-file-id="\${fileId}"] .file-checkbox\`);
                        const item = document.querySelector(\`[data-file-id="\${fileId}"]\`);
                        
                        if (selectedFiles.has(fileId)) {
                            selectedFiles.delete(fileId);
                            if (checkbox) checkbox.checked = false;
                            if (item) item.classList.remove('selected');
                        } else {
                            selectedFiles.add(fileId);
                            if (checkbox) checkbox.checked = true;
                            if (item) item.classList.add('selected');
                        }
                        updateUI();
                    }
                    
                    function updateUI() {
                        const count = selectedFiles.size;
                        document.getElementById('selectedCount').textContent = count === 0 ? '0 files selected' : \`\${count} file\${count === 1 ? '' : 's'} selected\`;
                        document.getElementById('selectBtn').disabled = count === 0;
                    }
                    
                    function updateBreadcrumb() {
                        document.getElementById('breadcrumb').innerHTML = pathStack.map((item, index) => \`
                            <span class="breadcrumb-item" onclick="navigateToPath(\${index})">\${item.name}</span>
                            \${index < pathStack.length - 1 ? '<span class="breadcrumb-separator">›</span>' : ''}
                        \`).join('');
                    }
                    
                    function navigateToPath(index) {
                        pathStack = pathStack.slice(0, index + 1);
                        loadFiles(pathStack[pathStack.length - 1].id);
                        updateBreadcrumb();
                    }
                    
                    async function loadFiles(folderId = 'root') {
                        document.getElementById('fileList').innerHTML = \`
                            <div class="loading">
                                <div class="loading-spinner"></div>
                                <div>Loading files...</div>
                            </div>
                        \`;
                        
                        try {
                            const files = await fetchFiles(folderId);
                            renderFiles(files);
                        } catch (error) {
                            document.getElementById('fileList').innerHTML = \`
                                <div class="loading">
                                    <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
                                    <div>Unable to load files</div>
                                    <div style="font-size: 12px; color: #a19f9d;">\${error.message}</div>
                                </div>
                            \`;
                        }
                    }
                    
                    function selectFiles() {
                        const selected = currentFiles.filter(f => selectedFiles.has(f.id));
                        const processedFiles = selected.map(file => ({
                            id: file.id,
                            name: file.name,
                            size: file.size || 0,
                            mimeType: file.file?.mimeType || 'application/octet-stream',
                            webUrl: file.webUrl,
                            lastModifiedDateTime: file.lastModifiedDateTime
                        }));
                        
                        window.opener.postMessage({
                            type: 'onedrive-files-selected',
                            files: processedFiles
                        }, '*');
                        
                        window.close();
                    }
                    
                    // Initial load
                    loadFiles();
                </script>
            </body>
            </html>
        `);
        pickerWindow.document.close();

        // Enhanced message listener for custom file browser
        const messageListener = (event) => {
            if (event.data.type === 'onedrive-files-selected') {
                if (event.data.files && Array.isArray(event.data.files) && event.data.files.length > 0) {
                    handleFilesPicked(event.data.files);
                    setError(`Selected ${event.data.files.length} file(s) successfully.`);
                } else {
                    setError("No files were selected.");
                }
                cleanup();
            }
        };

        const cleanup = () => {
            try {
                pickerWindow?.close();
                window.removeEventListener('message', messageListener);
                setIsPickerLoading(false);
            } catch (e) {
                console.warn('Cleanup error:', e);
            }
        };

        window.addEventListener('message', messageListener);

        // Check if window was closed
        const checkClosed = setInterval(() => {
            if (pickerWindow?.closed) {
                clearInterval(checkClosed);
                cleanup();
            }
        }, 1000);

        // Timeout after 60 seconds
        setTimeout(() => {
            if (pickerWindow && !pickerWindow.closed) {
                clearInterval(checkClosed);
                cleanup();
            }
        }, 60000);

    }, [accessToken]);

    // Handle files picked from OneDrive picker
    const handleFilesPicked = useCallback((pickedFiles) => {
        const fileIds = pickedFiles.map(file => file.id);
        console.log("Files picked from OneDrive:", pickedFiles);
        handleSync(fileIds);
    }, []);

    // Show loading state while checking permissions
    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    // Show access denied if no view permission
    if (!canView) {
        return (
            <div>
                <main className="content">
                    <div className="container-fluid p-0">
                        <Alert severity="error" sx={{ mt: 6 }}>
                            <strong>Access Denied</strong>
                            <br />
                            You don't have permission to view One drive Connector. Please contact your administrator.
                        </Alert>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle"><strong>OneDrive Connector</strong></h1>
                    </div>

                    {isLoading && <p>Loading configuration...</p>}
                    
                    {error && (
                        <div className={`alert ${error.includes("Success") || error.includes("successfully") ? "alert-success" : "alert-danger"} mt-3`} role="alert">
                            {error}
                        </div>
                    )}

                                 
                    {!isLoading && !isConnected  && !canModify && (
                        <div className="card">
                            <div className="card-body text-center">
                                <p>You don't have permission to connect onedrive. Please contact your administrator.</p>
                            </div>
                        </div>
                    )}


                    {/* Connection Card */}
                    {!isLoading && !isConnected && canModify &&  (
                        <div className="card">
                            <div className="card-body text-center">
                                <h4 className="mb-3">
                                    <i className="fa fa-microsoft me-2"></i>
                                    Connect to OneDrive
                                </h4>
                                <p>Connect your OneDrive account to select files for your chatbot.</p>
                                <button 
                                    className="btn btn-lg btn-primary" 
                                    onClick={handleConnect}
                                    disabled={isConnecting}
                                >
                                    {isConnecting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Connecting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-microsoft me-2"></i> 
                                            Connect to OneDrive
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Connected State */}
                    {!isLoading && isConnected && (
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-lg-8">
                                        <h5 className="mb-2">
                                            <i className="fa fa-microsoft me-2 text-success"></i>
                                            Connected to OneDrive
                                        </h5>
                                        <p className="mb-0">Select the row(s) containing the desired files, then click the "Scrape and Train" button. Once training is complete, the "Trained" column will display "Yes".</p>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="d-flex flex-column flex-sm-row gap-2">
                                            {canModify && (
                                                <>
                                                    <button 
                                                        className="btn btn-secondary" 
                                                        onClick={openOneDriveFilePicker} 
                                                        disabled={isPickerLoading}
                                                    >
                                                        {isPickerLoading ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                Loading...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fa fa-plus me-1"></i> 
                                                                Select Files from OneDrive
                                                            </>
                                                        )}
                                                    </button>
                                                    
                                                    <button 
                                                        className="btn btn-danger" 
                                                        onClick={handleDisconnect} 
                                                        disabled={isConnecting}
                                                    >
                                                        {isConnecting && !isConnected ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                Disconnecting...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fa fa-unlink me-1"></i> 
                                                                Disconnect
                                                            </>
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <ListOneDriveFiles refreshTrigger={refreshTrigger} chatbotId={paramsChatbotId} permission={{
                                        canView: canView,
                                        canModify: canModify,
                                        canDelete: canDelete
                                    }} />
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}